// src/pages/ActivityLog.jsx - Enhanced with pagination and date range filtering
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Archive,
  ArrowRight,
  Calendar,
  Check,
  ClipboardList,
  Download,
  Eye,
  EyeOff,
  Info,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  User,
  UserCheck,
  X
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export default function ActivityLogPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    action: "all",
    table: "all",
    user: "all",
    timeRange: "all",
    dateFrom: "",
    dateTo: ""
  });
  const [showDetails, setShowDetails] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // NEW: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // NEW: Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  const queryClient = useQueryClient();

  // NEW: Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load activity log data
  const { data: activities = [], isLoading: activitiesLoading, refetch } = useQuery({
    queryKey: ['activity_log', filters],
    queryFn: async () => {
      let query = supabase
        .from('activity_log')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.action !== "all") {
        query = query.eq('action', filters.action);
      }
      
      if (filters.table !== "all") {
        query = query.eq('table_name', filters.table);
      }
      
      if (filters.user !== "all") {
        query = query.eq('user_id', filters.user);
      }

      // NEW: Enhanced date filtering - use custom date range if provided, otherwise use timeRange
      if (filters.dateFrom && filters.dateTo) {
        // Use custom date range
        const fromDate = new Date(filters.dateFrom);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire end date
        
        query = query
          .gte('created_at', fromDate.toISOString())
          .lte('created_at', toDate.toISOString());
      } else if (filters.timeRange !== "all") {
        // Use predefined time range
        const now = new Date();
        let startDate;
        
        switch (filters.timeRange) {
          case '1d':
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }
        
        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }
      }

      // Load more records for pagination (but still limit for performance)
      query = query.limit(5000);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (activityIds) => {
      console.log('🗑️ Bulk deleting activities:', activityIds);
      
      const { error } = await supabase
        .from('activity_log')
        .delete()
        .in('id', activityIds);

      if (error) {
        console.error('❌ Bulk delete error:', error);
        throw error;
      }

      console.log('✅ Bulk delete successful');
      return activityIds;
    },
    onSuccess: (deletedIds) => {
      queryClient.invalidateQueries({ queryKey: ['activity_log'] });
      setSelectedItems(new Set());
      setShowDeleteConfirm(false);
      console.log(`✅ Successfully deleted ${deletedIds.length} activity records`);
    },
    onError: (error) => {
      console.error('💥 Bulk delete failed:', error);
      alert('Errore durante l\'eliminazione dei record. Riprova.');
    }
  });

  // Get unique users and tables for filters
  const uniqueUsers = [...new Set(activities.map(a => ({ id: a.user_id, name: a.user_name, email: a.user_email })))];
  const uniqueTables = [...new Set(activities.map(a => a.table_name))];

  // NEW: Enhanced filtering logic with search term
  const filteredActivities = useMemo(() => {
    return activities.filter(activity => {
      const matchesSearch = !searchTerm || 
        activity.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.table_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.record_identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.change_description?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [activities, searchTerm]);

  // NEW: Pagination calculations
  const totalPages = Math.ceil(filteredActivities.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedActivities = filteredActivities.slice(startIndex, endIndex);

  // NEW: Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, pageSize]);

  // NEW: Ensure current page is valid when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // NEW: Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // NEW: Date filter handlers
  const handleDateFromChange = (value) => {
    setFilters(prev => ({ 
      ...prev, 
      dateFrom: value,
      timeRange: value || filters.dateTo ? "custom" : "all"
    }));
  };

  const handleDateToChange = (value) => {
    setFilters(prev => ({ 
      ...prev, 
      dateTo: value,
      timeRange: filters.dateFrom || value ? "custom" : "all"
    }));
  };

  const clearDateFilters = () => {
    setFilters(prev => ({ 
      ...prev, 
      dateFrom: "",
      dateTo: "",
      timeRange: "all"
    }));
  };

  // Selection handlers - UPDATED to work with paginated data
  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all items on current page
      setSelectedItems(new Set([...selectedItems, ...paginatedActivities.map(activity => activity.id)]));
    } else {
      // Deselect all items on current page
      const currentPageIds = new Set(paginatedActivities.map(activity => activity.id));
      setSelectedItems(new Set([...selectedItems].filter(id => !currentPageIds.has(id))));
    }
  };

  const handleSelectItem = (activityId, checked) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(activityId);
    } else {
      newSelection.delete(activityId);
    }
    setSelectedItems(newSelection);
  };

  const handleBulkDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    const idsToDelete = Array.from(selectedItems);
    bulkDeleteMutation.mutate(idsToDelete);
  };

  const cancelBulkDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Helper functions
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/D";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return "N/D";
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE': return <Plus className="w-4 h-4 text-green-600" />;
      case 'UPDATE': return <UserCheck className="w-4 h-4 text-blue-600" />;
      case 'DELETE': return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'ARCHIVE': return <Archive className="w-4 h-4 text-orange-600" />;
      default: return <ClipboardList className="w-4 h-4 text-slate-600" />;
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800 border-green-200';
      case 'UPDATE': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'DELETE': return 'bg-red-100 text-red-800 border-red-200';
      case 'ARCHIVE': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getActionText = (action) => {
    switch (action) {
      case 'CREATE': return 'Creato';
      case 'UPDATE': return 'Modificato';
      case 'DELETE': return 'Eliminato';
      case 'ARCHIVE': return 'Archiviato';
      default: return action;
    }
  };

  const toggleDetails = (activityId) => {
    setShowDetails(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  // Enhanced function to render individual changes
  const renderChanges = (changes) => {
    if (!changes || !Array.isArray(changes) || changes.length === 0) {
      return <span className="text-slate-500 italic">Nessuna modifica registrata</span>;
    }

    return (
      <div className="space-y-3">
        {changes.map((change, index) => (
          <div key={index} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-slate-700">{change.fieldName}</span>
              <span className="text-xs text-slate-500">Campo: {change.field}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex-1">
                <div className="text-slate-600 mb-1">Da:</div>
                <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-red-800 font-mono text-xs">
                  {change.oldDisplay}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-slate-600 mb-1">A:</div>
                <div className="bg-green-50 border border-green-200 rounded px-2 py-1 text-green-800 font-mono text-xs">
                  {change.newDisplay}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const exportToCSV = () => {
    const headers = [
      'Data/Ora',
      'Utente',
      'Email',
      'Azione',
      'Tabella',
      'Record',
      'Descrizione Modifiche',
      'User Agent'
    ];

    // Export filtered data, not just paginated data
    const csvData = filteredActivities.map(activity => [
      formatDateTime(activity.created_at),
      activity.user_name || '',
      activity.user_email || '',
      activity.action || '',
      activity.table_name || '',
      activity.record_identifier || '',
      activity.change_description || '',
      activity.user_agent || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `activity-log-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // NEW: Check selection state for current page
  const currentPageIds = new Set(paginatedActivities.map(activity => activity.id));
  const selectedOnCurrentPage = [...selectedItems].filter(id => currentPageIds.has(id));
  const isAllSelectedOnPage = paginatedActivities.length > 0 && selectedOnCurrentPage.length === paginatedActivities.length;
  const isPartiallySelectedOnPage = selectedOnCurrentPage.length > 0 && selectedOnCurrentPage.length < paginatedActivities.length;

  // NEW: Check if custom date range is active
  const hasCustomDateRange = filters.dateFrom || filters.dateTo;

  if (activitiesLoading) {
    return (
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Caricamento log attività...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? 'p-3' : 'p-4 md:p-8'} min-h-screen`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`${isMobile ? 'space-y-4' : 'flex flex-col md:flex-row justify-between items-start md:items-center'} mb-8 gap-4`}>
          <div className={isMobile ? 'text-center' : ''}>
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-slate-800 mb-2 flex items-center ${isMobile ? 'justify-center' : ''} gap-3`}>
              <ClipboardList className="w-8 h-8 text-slate-600" />
              Log Attività
            </h1>
            <p className={`text-slate-600 ${isMobile ? 'text-sm' : ''}`}>
              Traccia tutte le attività degli utenti nel sistema
              {filteredActivities.length !== activities.length && (
                <span className={`ml-2 ${isMobile ? 'block mt-1' : 'text-sm'} text-blue-600`}>
                  ({filteredActivities.length} di {activities.length} attività visualizzate)
                </span>
              )}
            </p>
          </div>
          <div className={`flex items-center ${isMobile ? 'flex-col space-y-2 w-full' : 'gap-3'}`}>
            <Button
              onClick={exportToCSV}
              variant="outline"
              className={`bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-sm ${isMobile ? 'w-full' : ''}`}
              disabled={filteredActivities.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV {filteredActivities.length !== activities.length && `(${filteredActivities.length})`}
            </Button>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className={`bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-sm ${isMobile ? 'w-full' : ''}`}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        <AnimatePresence>
          {selectedItems.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 ${isMobile ? 'mx-1' : ''}`}
            >
              <div className={`flex items-center ${isMobile ? 'flex-col space-y-3' : 'justify-between'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">
                      {selectedItems.size} {selectedItems.size === 1 ? 'elemento selezionato' : 'elementi selezionati'}
                    </span>
                    <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-600`}>
                      Seleziona le azioni da eseguire sui record selezionati
                    </p>
                  </div>
                </div>
                <div className={`flex items-center ${isMobile ? 'w-full flex-col space-y-2' : 'gap-2'}`}>
                  <Button
                    onClick={() => setSelectedItems(new Set())}
                    variant="outline"
                    size="sm"
                    className={`border-blue-300 text-blue-700 hover:bg-blue-100 ${isMobile ? 'w-full' : ''}`}
                  >
                    Deseleziona tutto
                  </Button>
                  <Button
                    onClick={handleBulkDelete}
                    variant="destructive"
                    size="sm"
                    disabled={bulkDeleteMutation.isPending}
                    className={`bg-red-500 hover:bg-red-600 ${isMobile ? 'w-full' : ''}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {bulkDeleteMutation.isPending ? 'Eliminando...' : 'Elimina selezionati'}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ENHANCED: Search and Filters with Date Range */}
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6 mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <Input
                placeholder={isMobile ? "Cerca attività..." : "Cerca per utente, azione, tabella, modifiche..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Filter Grid */}
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'} mb-4`}>
            {/* Action Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Azione</label>
              <Select value={filters.action} onValueChange={(value) => setFilters(prev => ({ ...prev, action: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Azione" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  <SelectItem value="all">Tutte le azioni</SelectItem>
                  <SelectItem value="CREATE">Creazione</SelectItem>
                  <SelectItem value="UPDATE">Modifica</SelectItem>
                  <SelectItem value="DELETE">Eliminazione</SelectItem>
                  <SelectItem value="ARCHIVE">Archiviazione</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tabella</label>
              <Select value={filters.table} onValueChange={(value) => setFilters(prev => ({ ...prev, table: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Tabella" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  <SelectItem value="all">Tutte le tabelle</SelectItem>
                  {uniqueTables.map(table => (
                    <SelectItem key={table} value={table}>{table}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Range Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Periodo</label>
              <Select 
                value={hasCustomDateRange ? "custom" : filters.timeRange} 
                onValueChange={(value) => {
                  if (value !== "custom") {
                    setFilters(prev => ({ ...prev, timeRange: value, dateFrom: "", dateTo: "" }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  <SelectItem value="all">Tutto il periodo</SelectItem>
                  <SelectItem value="1d">Ultimo giorno</SelectItem>
                  <SelectItem value="7d">Ultimi 7 giorni</SelectItem>
                  <SelectItem value="30d">Ultimi 30 giorni</SelectItem>
                  {hasCustomDateRange && <SelectItem value="custom">Intervallo personalizzato</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* NEW: Date From */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data da</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                className="w-full"
              />
            </div>

            {/* NEW: Date To */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data a</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleDateToChange(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* NEW: Clear Date Filters Button */}
          {hasCustomDateRange && (
            <div className="flex justify-start">
              <Button
                onClick={clearDateFilters}
                variant="outline"
                size="sm"
                className="text-slate-600"
              >
                <X className="w-4 h-4 mr-2" />
                Cancella filtri data
              </Button>
            </div>
          )}
        </div>

        {/* NEW: Pagination Controls - Top */}
        {totalPages > 1 && (
          <div className={`mb-4 ${isMobile ? 'px-1' : ''}`}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredActivities.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
              showPageSizeSelector={!isMobile}
              showInfo={!isMobile}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Activity Log Table */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          {filteredActivities.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Nessuna attività trovata</h3>
              <p className="text-slate-500">Non ci sono attività che corrispondono ai filtri selezionati.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-700 w-12">
                        <input
                          type="checkbox"
                          checked={isAllSelectedOnPage}
                          ref={(el) => {
                            if (el) el.indeterminate = isPartiallySelectedOnPage;
                          }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                      </th>
                      <th className="text-left p-4 font-medium text-slate-700">Data/Ora</th>
                      <th className="text-left p-4 font-medium text-slate-700">Utente</th>
                      <th className="text-left p-4 font-medium text-slate-700">Azione</th>
                      <th className="text-left p-4 font-medium text-slate-700">Tabella</th>
                      <th className="text-left p-4 font-medium text-slate-700">Record</th>
                      <th className="text-left p-4 font-medium text-slate-700">Modifiche</th>
                      <th className="text-left p-4 font-medium text-slate-700">Dettagli</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedActivities.map((activity, index) => (
                      <motion.tr
                        key={activity.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`hover:bg-slate-50 transition-colors ${
                          selectedItems.has(activity.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(activity.id)}
                            onChange={(e) => handleSelectItem(activity.id, e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-800">
                              {formatDateTime(activity.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <div>
                              <div className="text-sm font-medium text-slate-800">
                                {activity.user_name || 'N/D'}
                              </div>
                              <div className="text-xs text-slate-500">
                                {activity.user_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getActionBadgeColor(activity.action)}`}>
                            {getActionIcon(activity.action)}
                            <span className="ml-1">{getActionText(activity.action)}</span>
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-800 font-mono bg-slate-100 px-2 py-1 rounded">
                            {activity.table_name}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-800 font-medium">
                            {activity.record_identifier || 'N/D'}
                          </span>
                        </td>
                        <td className="p-4 max-w-md">
                          {activity.change_description ? (
                            <div className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                              {activity.change_description}
                            </div>
                          ) : activity.action === 'CREATE' ? (
                            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                              Nuovo record creato
                            </div>
                          ) : activity.action === 'ARCHIVE' ? (
                            <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                              Record archiviato
                            </div>
                          ) : (
                            <span className="text-sm text-slate-500 italic">Nessuna descrizione</span>
                          )}
                        </td>
                        <td className="p-4">
                          <Button
                            onClick={() => toggleDetails(activity.id)}
                            variant="outline"
                            size="sm"
                            className="hover:bg-slate-100"
                          >
                            {showDetails[activity.id] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>
                    Totale attività: {filteredActivities.length}
                    {selectedItems.size > 0 && (
                      <span className="ml-4 text-blue-600 font-medium">
                        • {selectedItems.size} selezionati
                      </span>
                    )}
                  </span>
                  <span>
                    {isMobile ? (
                      <>Pag. {currentPage} di {totalPages}</>
                    ) : (
                      <>Visualizzando {startIndex + 1}-{Math.min(endIndex, filteredActivities.length)} di {filteredActivities.length}</>
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* NEW: Pagination Controls - Bottom */}
        {totalPages > 1 && (
          <div className={`mt-6 ${isMobile ? 'px-1' : ''}`}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredActivities.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={isMobile ? [10, 25] : [10, 25, 50, 100]}
              showPageSizeSelector={false}
              showInfo={false}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* NEW: Quick Stats Summary for Mobile */}
        {isMobile && filteredActivities.length > 0 && (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border text-center">
            <p className="text-sm text-slate-600">
              Visualizzando <span className="font-semibold text-slate-800">{paginatedActivities.length}</span> di{' '}
              <span className="font-semibold text-slate-800">{filteredActivities.length}</span> attività
              {totalPages > 1 && (
                <span className="block mt-1">
                  Pagina {currentPage} di {totalPages}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">
                      Conferma eliminazione
                    </h3>
                    <p className="text-sm text-slate-600">
                      Questa azione non può essere annullata
                    </p>
                  </div>
                </div>
                
                <p className="text-slate-700 mb-6">
                  Sei sicuro di voler eliminare <strong>{selectedItems.size}</strong> {' '}
                  {selectedItems.size === 1 ? 'record del log' : 'record del log'}?
                  <br />
                  <span className="text-sm text-slate-500 mt-2 block">
                    I record eliminati non potranno essere recuperati.
                  </span>
                </p>
                
                <div className="flex gap-3 justify-end">
                  <Button
                    onClick={cancelBulkDelete}
                    variant="outline"
                    disabled={bulkDeleteMutation.isPending}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={confirmBulkDelete}
                    variant="destructive"
                    disabled={bulkDeleteMutation.isPending}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {bulkDeleteMutation.isPending ? 'Eliminando...' : 'Elimina definitivamente'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enhanced Details Panels */}
        {Object.keys(showDetails).filter(id => showDetails[id]).map(activityId => {
          const activity = activities.find(a => a.id === activityId);
          if (!activity) return null;

          return (
            <motion.div
              key={activityId}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 bg-white rounded-lg border border-slate-200 shadow-sm"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Info className="w-6 h-6 text-slate-600" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    Dettagli Attività - {activity.record_identifier}
                  </h3>
                </div>
                
                {/* Change Description Summary */}
                {activity.change_description && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">Riepilogo Modifiche</h4>
                    <p className="text-blue-700">{activity.change_description}</p>
                  </div>
                )}
                
                <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 lg:grid-cols-2 gap-6'}`}>
                  {/* Detailed Changes */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Modifiche Dettagliate
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4">
                      {renderChanges(activity.changes)}
                    </div>
                  </div>

                  {/* Technical Details */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Dettagli Tecnici
                    </h4>
                    <div className="space-y-3 text-sm bg-slate-50 rounded-lg p-4">
                      <div className="flex justify-between">
                        <span className="font-medium text-slate-600">ID Record:</span>
                        <span className="font-mono text-slate-800">{activity.record_id || 'N/D'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-slate-600">ID Utente:</span>
                        <span className="font-mono text-slate-800">{activity.user_id || 'N/D'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-slate-600">IP Address:</span>
                        <span className="font-mono text-slate-800">{activity.ip_address || 'N/D'}</span>
                      </div>
                      {activity.user_agent && (
                        <div>
                          <span className="font-medium text-slate-600 block mb-1">User Agent:</span>
                          <div className="text-slate-800 break-all bg-white p-2 rounded text-xs font-mono">
                            {activity.user_agent}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* NEW: Bottom spacing */}
        <div className={isMobile ? 'h-16' : 'h-8'}></div>
      </div>
    </div>
  );
}